import React, { useMemo } from 'react'
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts'
import { TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE } from '../rejections/chartTheme'
import { money, fmtShortDay } from '../financial/formatting'
import { earningsRows } from './model'

const SEGMENTS = {
  api: { label: 'API leads', color: 'var(--channel-api)' },
  clicks: { label: 'Clicks', color: 'var(--channel-clicks)' },
}

// Earnings per New York day, API at the bottom and clicks on top, each in
// its channel's colour. Only the channels the partner has are stacked.
export default function DailyEarningsChart({ daily, channels, height = 240 }) {
  const rows = useMemo(() => earningsRows(daily), [daily])
  const keys = ['api', 'clicks'].filter((k) => channels?.[k])
  if (keys.length === 0) return null

  return (
    <section className="pw-card" aria-label="Daily earnings" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 className="pw-title" style={{ fontSize: 15, fontWeight: 700 }}>Daily earnings</h2>
        <ul aria-label="Legend" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: 0, padding: 0, listStyle: 'none' }}>
          {keys.map((k) => (
            <li key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
              <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: SEGMENTS[k].color }} />
              {SEGMENTS[k].label}
            </li>
          ))}
        </ul>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="date" tickFormatter={fmtShortDay} minTickGap={12}
            tick={{ fontSize: 10, fill: 'var(--chart-axis)' }} axisLine={false} tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => `$${Number(v).toLocaleString('en-US', { notation: 'compact' })}`}
            tick={{ fontSize: 10, fill: 'var(--chart-axis)' }} axisLine={false} tickLine={false} width={48}
          />
          <Tooltip
            cursor={{ fill: 'var(--chart-cursor)' }}
            contentStyle={TOOLTIP_STYLE}
            labelStyle={TOOLTIP_LABEL_STYLE}
            labelFormatter={fmtShortDay}
            formatter={(value, name) => [money(value), SEGMENTS[name]?.label || name]}
          />
          {keys.map((k, i) => (
            <Bar
              key={k} dataKey={k} name={k} stackId="earnings" fill={SEGMENTS[k].color}
              radius={i === keys.length - 1 ? [3, 3, 0, 0] : 0}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </section>
  )
}
